<div *ngIf="company; else companyLoading">
  <div class="tooltip-header">
    {{company.name}}
    <span class="ticker" *ngIf="company.ticker">({{ company.ticker }})</span>
  </div>
  <div class="tooltip-body">
    <div>As Of End: <span class="bold">{{companyFinancial.asOfEndDate}}</span></div>
    <div class="d-flex" *ngIf="showPriceTarget">
      <div class="flex-fill bold">{{(companyFinancial?.longPrice || 0) | rmsNumberTransform: false: 2: '': company.currency}}</div>
      <div class="flex-fill pl-1 pr-1"><i class="fa fa-arrow-left text-success"></i></div>
      <ng-container *ngIf="companyFinancial?.useCalculatedFairValue; else manualFairValue">
        <div class="flex-fill bold text-teal">{{(companyFinancial?.calculatedFairPrice || 0) | rmsNumberTransform: false: 2: '': company.currency}}</div>
      </ng-container>
      <ng-template #manualFairValue>
        <div class="flex-fill bold">{{(companyFinancial?.fairPrice || 0) | rmsNumberTransform: false: 2: '': company.currency}}</div>
      </ng-template>
      <div class="flex-fill pl-1 pr-1"><i class="fa fa-arrow-right text-success"></i></div>
      <div class="flex-fill bold">{{(companyFinancial?.shortPrice || 0) | rmsNumberTransform: false: 2: '': company.currency}}</div>
    </div>
    <div>
      Cash:
      <span class="bold">{{companyFinancial?.cash | rmsFormatMoney: company.currency}}</span>
      <span *ngIf="companyFinancial?.oneYearOfCash && companyFinancial?.burn > 0">1Y &#64; {{companyFinancial?.oneYearOfCash}}</span>
    </div>
    <div>
      {{companyFinancial?.burn < 0 ? 'FCF:' : 'Burn:'}}
      <span class="bold">{{Math.abs(companyFinancial?.burn) | rmsFormatMoney: company.currency}}</span>
      <span *ngIf="companyFinancial?.yearsOfCash && companyFinancial?.burn > 0">({{companyFinancial?.yearsOfCash?.toFixed(1)}}Y)</span>
    </div>
    <div>Shares:
      <span *ngIf="companyFinancial?.shares" class="bold">{{companyFinancial?.shares | rmsFormatMoney}}</span>
      <sup class="share-sup-tsm" *ngIf="companyFinancial?.treasuryStockMethod">TSM</sup>
    </div>
    <div *ngIf="!company.isPrivate">Market Cap: <span class="bold">{{companyFinancial?.marketCap | rmsFormatMoney: company.currency}}</span></div>
    <div *ngIf="companyFinancial?.wiwiiw">WIWIIW: <span class="bold">{{companyFinancial?.wiwiiw | rmsFormatMoney: company.currency}}</span></div>
    <div *ngIf="percentFund">%-F: <span class="bold">{{percentFund | rmsNumberTransform: false: 1: '%'}}</span></div>
    <div class="d-flex">
      <ng-container *ngIf="latestNote">
        <div>
          Latest Note:
          <span class="bold">{{latestNote.textContent.timestamp | rmsTzFormat: formatEST: shortDateFormat}}</span> <br> <span class="ml-2">by</span> <span class="bold">{{latestNote.textContent.createdBy.firstName}} {{latestNote.textContent.createdBy.lastName}}</span>
        </div>
        <img class="latest-note-icon" *ngIf="latestNote.textContent.isOverview" src="assets/icons/flask.svg" alt="Overview Note" />
        <img class="latest-note-icon" *ngIf="crmNoteService.isCrmNote(latestNote.textContent)" src="assets/icons/lightning-bolt.svg" alt="Sonar Note" />
      </ng-container>
      <ng-container *ngIf="noteLoading && !latestNote">
        <div>Latest Note: <span class="bold">Loading...</span></div>
      </ng-container>
      <ng-container *ngIf="!noteLoading && !latestNote">
        <div>Latest Note: <span class="bold">No company notes</span></div>
      </ng-container>
    </div>

    <div class="tooltip-portfolios-info" *ngIf="company.aggregatePosition && hasPermission['corePortfolioRead']">
      <div class="tooltip-positions">
        <div *ngIf="!isFullyBoxedMode" id="tooltip-positions-chart" echarts [options]="chartOptions" (chartInit)="onChartInit($event)"></div>

        <div *ngFor="let portfolio of ((company.aggregatePosition.fundValues || {}) | keyvalue)" class="tooltip-portfolio-position">
          <div class="tooltip-position-label main-label">
            <div class="tooltip-position-color-box" [ngStyle]="{'background-color': PORTFOLIO_COLORS[portfolio.key]}"></div>
            <div class="ml-2">{{((getPortfolioValue(portfolio.key) / totalPositionsValue * 100) || 0) | number:'1.0-0'}}%</div>
            <div class="mx-1">|</div>
            <div>{{getPortfolioValue(portfolio.key) | rmsFormatMinMillionMoney:company.currency}}</div>
          </div>
          <div class="d-flex align-items-center">
            {{portfolio.value[0].portfolioName}}
            <img *ngIf="isPortfolioFullyBoxed(portfolio.key)" class="boxed-icon ml-1"
                 src="assets/icons/fully-boxed-position.svg" alt="Fully Boxed Position"/>
          </div>
          <ng-container *ngIf="portfolio.value.length > 1">
            <ng-container *ngFor="let position of portfolio.value | rmsSortBy:'portfolioValue':'DESC'">
              <div class="tooltip-position-label sub-label ml-2">
                <div class="tooltip-position-color-box" [ngStyle]="{'background-color': PORTFOLIO_COLORS[portfolio.key]}"></div>
                <div class="ml-2 mr-1">{{position.portfolioValue | rmsFormatMinMillionMoney:company.currency}}</div>
                <div>{{position.stockType === 'CVR' ? position.stockType : (getStockType(position.stockType) | titlecase)}}</div>
              </div>
            </ng-container>
          </ng-container>
        </div>
      </div>

      <div class="tooltip-summary">
        <div class="tooltip-summary-data" *ngIf="!isFullyBoxedMode">
          <div class="label">Avg. Cost:</div>
          <div class="data">{{company.aggregatePosition.costBasis | rmsFormatMoney:company.currency:2}}</div>
        </div>

        <div class="tooltip-summary-data" *ngIf="company.isPrivate">
          <div class="label">Avg. Price:</div>
          <div class="data">{{company.aggregatePosition.manualPrice | rmsFormatMoney:company.currency:2}}</div>
        </div>

        <div class="tooltip-summary-data" *ngIf="!isFullyBoxedMode">
          <div class="label">Total Shares:</div>
          <div class="data">{{company.aggregatePosition.shares | number}}</div>
        </div>

        <div class="tooltip-summary-data">
          <div class="label">Total Value:</div>
          <div class="data">{{totalPositionsValue | rmsFormatMinMillionMoney: company.currency}}</div>
        </div>

        <div class="tooltip-summary-data" *ngIf="!company.isPrivate && !isFullyBoxedMode">
          <div class="label">Total P/L:</div>
          <div class="data" [ngClass]="{ 'text-success': company.aggregatePosition.totalPL > 0, 'text-danger': company.aggregatePosition.totalPL < 0 }">
            {{company.aggregatePosition.totalPL | number:'1.0-0'}}%
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<ng-template #companyLoading>
  <div class="empty-tooltip">
    <rms-loading-spinner class="tooltip-loading-spinner"></rms-loading-spinner>
  </div>
</ng-template>
